"use client";

import { createContext, useContext, useCallback, useEffect, useMemo, useSyncExternalStore } from "react";
import { syncCartToServer, getServerCartForMerge } from "@/app/(public)/store/_actions";
import { useToast } from "@shared/ToastContext";

const CartContext = createContext(null);
const STORAGE_KEY = "velanapps_store_cart";
const DISCOUNT_KEY = "velanapps_store_discount";
const EVENT_NAME = "store-cart-updated";

function readCart() {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function writeCart(items) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  } catch {
    // ignore quota/unavailable localStorage
  }
  window.dispatchEvent(new Event(EVENT_NAME));
  syncCartToServer(items).catch(() => {});
}

function writeDiscount(discount) {
  try {
    if (discount) window.localStorage.setItem(DISCOUNT_KEY, JSON.stringify(discount));
    else window.localStorage.removeItem(DISCOUNT_KEY);
  } catch {
    // ignore quota/unavailable localStorage
  }
  window.dispatchEvent(new Event(EVENT_NAME));
}

function subscribe(callback) {
  window.addEventListener("storage", callback);
  window.addEventListener(EVENT_NAME, callback);
  return () => {
    window.removeEventListener("storage", callback);
    window.removeEventListener(EVENT_NAME, callback);
  };
}

function getSnapshot() {
  return window.localStorage.getItem(STORAGE_KEY) ?? "[]";
}

function getDiscountSnapshot() {
  return window.localStorage.getItem(DISCOUNT_KEY) ?? "null";
}

function getServerSnapshot() {
  return "[]";
}

function getDiscountServerSnapshot() {
  return "null";
}

export function CartProvider({ children }) {
  const { showToast } = useToast();
  const raw = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
  const items = useMemo(() => {
    try {
      return JSON.parse(raw) ?? [];
    } catch {
      return [];
    }
  }, [raw]);

  const discountRaw = useSyncExternalStore(subscribe, getDiscountSnapshot, getDiscountServerSnapshot);
  const discount = useMemo(() => {
    try {
      return JSON.parse(discountRaw);
    } catch {
      return null;
    }
  }, [discountRaw]);

  useEffect(() => {
    syncCartToServer(readCart()).catch(() => {});
  }, []);

  const addToCart = useCallback((product, quantity = 1, options = {}) => {
    const { customFields, unitPrice } = options;
    const price = unitPrice ?? product.price;
    const current = readCart();
    const hasCustomFields = !!customFields && Object.keys(customFields).length > 0;
    const fieldsKey = hasCustomFields ? JSON.stringify(customFields) : null;

    const existing = current.find((i) => {
      if ((i.slug ?? i.id) !== product.slug) return false;
      const iKey = i.customFields ? JSON.stringify(i.customFields) : null;
      return iKey === fieldsKey;
    });

    const next = existing
      ? current.map((i) => (i === existing ? { ...i, quantity: i.quantity + quantity } : i))
      : [
          ...current,
          {
            id: hasCustomFields ? `${product.slug}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}` : product.slug,
            slug: product.slug,
            title: product.title,
            sku: product.sku ?? null,
            price,
            quantity,
            image: product.images?.[0] ?? null,
            ...(hasCustomFields ? { customFields } : {}),
          },
        ];
    writeCart(next);
    showToast(
      existing
        ? `Quantity updated — ${product.title} is already in your cart`
        : `Added to cart — ${product.title}`
    );
  }, [showToast]);

  const removeFromCart = useCallback((id) => {
    writeCart(readCart().filter((i) => (i.id ?? i.slug) !== id));
  }, []);

  const removeItems = useCallback((ids) => {
    const idSet = new Set(ids);
    writeCart(readCart().filter((i) => !idSet.has(i.id ?? i.slug)));
  }, []);

  const updateQuantity = useCallback((id, quantity) => {
    const current = readCart();
    const next = quantity <= 0
      ? current.filter((i) => (i.id ?? i.slug) !== id)
      : current.map((i) => ((i.id ?? i.slug) === id ? { ...i, quantity } : i));
    writeCart(next);
  }, []);

  const updateCartItem = useCallback((id, patch) => {
    const current = readCart();
    const next = current.map((i) => ((i.id ?? i.slug) === id ? { ...i, ...patch } : i));
    writeCart(next);
  }, []);

  const mergeServerCart = useCallback(async () => {
    let serverItems;
    try {
      serverItems = await getServerCartForMerge();
    } catch {
      return;
    }
    if (!Array.isArray(serverItems) || serverItems.length === 0) return;

    const current = readCart();
    const next = [...current];
    for (const serverItem of serverItems) {
      const fieldsKey = serverItem.customFields ? JSON.stringify(serverItem.customFields) : null;
      const existing = next.find((i) => {
        if ((i.slug ?? i.id) !== serverItem.slug) return false;
        const iKey = i.customFields ? JSON.stringify(i.customFields) : null;
        return iKey === fieldsKey;
      });
      if (existing) {
        existing.quantity += serverItem.quantity;
      } else {
        const hasCustomFields = !!serverItem.customFields;
        next.push({
          ...serverItem,
          id: hasCustomFields ? `${serverItem.slug}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}` : serverItem.slug,
        });
      }
    }
    writeCart(next);
  }, []);

  // Discount clears with the cart — a stale code shouldn't silently apply to
  // whatever a shopper builds up next.
  const clearCart = useCallback(() => {
    writeCart([]);
    writeDiscount(null);
  }, []);

  const applyDiscount = useCallback((nextDiscount) => writeDiscount(nextDiscount), []);
  const removeDiscount = useCallback(() => writeDiscount(null), []);

  const cartCount = items.reduce((sum, i) => sum + i.quantity, 0);
  const cartTotal = items.reduce((sum, i) => sum + i.price * i.quantity, 0);

  const value = useMemo(
    () => ({
      items, addToCart, removeFromCart, removeItems, updateQuantity, updateCartItem, clearCart, cartCount, cartTotal,
      discount, applyDiscount, removeDiscount, mergeServerCart,
    }),
    [items, addToCart, removeFromCart, removeItems, updateQuantity, updateCartItem, clearCart, cartCount, cartTotal, discount, applyDiscount, removeDiscount, mergeServerCart]
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

const CART_FALLBACK = {
  items: [],
  addToCart: () => {},
  removeFromCart: () => {},
  removeItems: () => {},
  updateQuantity: () => {},
  updateCartItem: () => {},
  clearCart: () => {},
  cartCount: 0,
  cartTotal: 0,
  discount: null,
  applyDiscount: () => {},
  removeDiscount: () => {},
  mergeServerCart: async () => {},
};

export function useCart() {
  const ctx = useContext(CartContext);
  return ctx ?? CART_FALLBACK;
}
